import { decodeJsonObject, postLocalJson } from "./localJsonTransport";
// World conversations through the local server's grant door (CM4 over the
// CM3 lane). The browser names a World and plain conversation fields; the
// Actor, the bearer, and participants-only access live server-side in the
// grant and the World authority. Every failure arrives as the lane's typed
// refusal envelope and is surfaced as `{ kind, reason }` — never thrown.

export interface ConversationSummary {
  readonly conversationId: string;
  readonly participantIds: ReadonlyArray<string>;
  readonly createdAt: number;
  // CM17: the door's CM5 read-state projections, tolerated as absent so
  // older fixtures and pre-widening doors still parse.
  readonly lastSeq?: number;
  readonly lastMessageAt?: number;
  readonly unreadCount?: number;
}

export interface ConversationMessage {
  readonly authorActorId: string;
  readonly body: string;
  readonly seq: number;
  readonly createdAt: number;
}

/** CM5's per-participant receipt preference, as the door serves it. */
export type ReceiptVisibility = "shared" | "private";

/** CM31 — the caller's own CM5 read-state row from the read answer. */
export interface ConversationSelfReadState {
  readonly lastReadSeq: number;
  readonly receiptVisibility: ReceiptVisibility;
}

export interface ConversationRead extends ConversationSummary {
  readonly messages: ReadonlyArray<ConversationMessage>;
  /** Absent when an older door serves no readState; nothing is faked. */
  readonly self?: ConversationSelfReadState;
}

export interface ConversationMember {
  readonly actorId: string;
  readonly displayName: string;
  readonly isSelf: boolean;
}

export interface CommsLaneRefusal {
  readonly ok: false;
  readonly kind: string;
  readonly reason: string;
}

export type ConversationListOutcome =
  | { readonly ok: true; readonly conversations: ReadonlyArray<ConversationSummary> }
  | CommsLaneRefusal;

export type ConversationMembersOutcome =
  | { readonly ok: true; readonly members: ReadonlyArray<ConversationMember> }
  | CommsLaneRefusal;

export type ConversationCreateOutcome =
  | { readonly ok: true; readonly conversation: ConversationSummary; readonly created: boolean }
  | CommsLaneRefusal;

export type ConversationReadOutcome =
  | { readonly ok: true; readonly conversation: ConversationRead }
  | CommsLaneRefusal;

export type ConversationSendOutcome =
  | { readonly ok: true; readonly seq: number }
  | CommsLaneRefusal;

function refusal(response: Response, body: Record<string, unknown> | null): CommsLaneRefusal {
  return {
    ok: false,
    kind: typeof body?.["kind"] === "string" ? body["kind"] : `http_${response.status}`,
    reason:
      typeof body?.["reason"] === "string"
        ? body["reason"]
        : "World conversations are unavailable.",
  };
}

async function call(
  path: string,
  payload: Record<string, unknown>,
  fetchImpl: typeof fetch,
): Promise<Record<string, unknown> | CommsLaneRefusal> {
  let response: Response;
  try {
    response = await postLocalJson(path, payload, fetchImpl);
  } catch {
    return { ok: false, kind: "transport", reason: "The local Cleo server is unreachable." };
  }
  const body = await decodeJsonObject(response);
  if (!response.ok || body?.["ok"] !== true) return refusal(response, body);
  return body;
}

function isRefusal(value: Record<string, unknown> | CommsLaneRefusal): value is CommsLaneRefusal {
  return value["ok"] === false;
}

function parsedSummary(value: unknown): ConversationSummary | null {
  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;
  if (typeof row["conversationId"] !== "string") return null;
  if (
    !Array.isArray(row["participantIds"]) ||
    row["participantIds"].some((id) => typeof id !== "string")
  ) {
    return null;
  }
  if (typeof row["createdAt"] !== "number") return null;
  return {
    conversationId: row["conversationId"],
    participantIds: row["participantIds"] as string[],
    createdAt: row["createdAt"],
    ...(typeof row["lastSeq"] === "number" ? { lastSeq: row["lastSeq"] } : {}),
    ...(typeof row["lastMessageAt"] === "number" ? { lastMessageAt: row["lastMessageAt"] } : {}),
    ...(typeof row["unreadCount"] === "number" ? { unreadCount: row["unreadCount"] } : {}),
  };
}

function parsedMessage(value: unknown): ConversationMessage | null {
  if (typeof value !== "object" || value === null) return null;
  const row = value as Record<string, unknown>;
  if (typeof row["authorActorId"] !== "string" || typeof row["body"] !== "string") return null;
  if (typeof row["seq"] !== "number" || typeof row["createdAt"] !== "number") return null;
  return {
    authorActorId: row["authorActorId"],
    body: row["body"],
    seq: row["seq"],
    createdAt: row["createdAt"],
  };
}

export async function listConversations(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ConversationListOutcome> {
  const outcome = await call("/api/neucleos/comms/list", { worldKey }, fetchImpl);
  if (isRefusal(outcome)) return outcome;
  const rows = outcome["conversations"];
  if (!Array.isArray(rows)) {
    return {
      ok: false,
      kind: "transport",
      reason: "The conversations lane returned an invalid list.",
    };
  }
  const conversations: ConversationSummary[] = [];
  for (const row of rows) {
    const summary = parsedSummary(row);
    if (summary === null) {
      return {
        ok: false,
        kind: "transport",
        reason: "The conversations lane returned an invalid row.",
      };
    }
    conversations.push(summary);
  }
  return { ok: true, conversations };
}

export async function listConversationMembers(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ConversationMembersOutcome> {
  const outcome = await call("/api/neucleos/comms/members", { worldKey }, fetchImpl);
  if (isRefusal(outcome)) return outcome;
  const rows = outcome["members"];
  if (!Array.isArray(rows)) {
    return {
      ok: false,
      kind: "transport",
      reason: "The conversations lane returned an invalid member list.",
    };
  }
  const members: ConversationMember[] = [];
  for (const value of rows) {
    if (typeof value !== "object" || value === null) {
      return {
        ok: false,
        kind: "transport",
        reason: "The conversations lane returned an invalid member row.",
      };
    }
    const row = value as Record<string, unknown>;
    if (
      typeof row["actorId"] !== "string" ||
      typeof row["displayName"] !== "string" ||
      typeof row["isSelf"] !== "boolean"
    ) {
      return {
        ok: false,
        kind: "transport",
        reason: "The conversations lane returned an invalid member row.",
      };
    }
    members.push({
      actorId: row["actorId"],
      displayName: row["displayName"],
      isSelf: row["isSelf"],
    });
  }
  return { ok: true, members };
}

export async function createConversation(
  worldKey: string,
  participantIds: ReadonlyArray<string>,
  fetchImpl: typeof fetch = fetch,
): Promise<ConversationCreateOutcome> {
  const outcome = await call("/api/neucleos/comms/create", { worldKey, participantIds }, fetchImpl);
  if (isRefusal(outcome)) return outcome;
  const conversation = parsedSummary(outcome);
  if (conversation === null || typeof outcome["created"] !== "boolean") {
    return {
      ok: false,
      kind: "transport",
      reason: "The conversations lane returned an invalid create receipt.",
    };
  }
  return { ok: true, conversation, created: outcome["created"] };
}

export async function readConversation(
  worldKey: string,
  conversationId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ConversationReadOutcome> {
  const outcome = await call("/api/neucleos/comms/read", { worldKey, conversationId }, fetchImpl);
  if (isRefusal(outcome)) return outcome;
  const summary = parsedSummary(outcome);
  const rows = outcome["messages"];
  if (summary === null || !Array.isArray(rows)) {
    return {
      ok: false,
      kind: "transport",
      reason: "The conversations lane returned an invalid conversation.",
    };
  }
  const messages: ConversationMessage[] = [];
  for (const row of rows) {
    const message = parsedMessage(row);
    if (message === null) {
      return {
        ok: false,
        kind: "transport",
        reason: "The conversations lane returned an invalid message.",
      };
    }
    messages.push(message);
  }
  const readState = outcome["readState"];
  let self: ConversationSelfReadState | undefined;
  if (typeof readState === "object" && readState !== null && !Array.isArray(readState)) {
    const row = (readState as Record<string, unknown>)["self"];
    if (typeof row === "object" && row !== null && !Array.isArray(row)) {
      const record = row as Record<string, unknown>;
      if (
        typeof record["lastReadSeq"] === "number" &&
        (record["receiptVisibility"] === "shared" || record["receiptVisibility"] === "private")
      ) {
        self = {
          lastReadSeq: record["lastReadSeq"],
          receiptVisibility: record["receiptVisibility"],
        };
      }
    }
  }
  return {
    ok: true,
    conversation: { ...summary, messages, ...(self === undefined ? {} : { self }) },
  };
}

// ————— CM17 additions: read-state, presence, and the email rails over the
// CM16 lane routes. Every function keeps the lane law: typed refusal
// envelopes surfaced as values, never thrown; tolerant row parsing.

export interface PresenceParticipant {
  readonly actorId: string;
  readonly online: boolean;
}

export type PresenceListOutcome =
  | { readonly ok: true; readonly participants: ReadonlyArray<PresenceParticipant> }
  | CommsLaneRefusal;

export interface EmailAccountSummary {
  readonly accountId: string;
  readonly address: string;
  readonly status: string;
}

export interface EmailThreadSummary {
  readonly threadId: string;
  readonly subject: string;
  readonly lastMessageAt: number;
  readonly accountAddress: string;
  /** CM24: the owning account's id — identity for the selection, never
   * presentation. */
  readonly accountId: string;
}

export type EmailAccountsOutcome =
  | { readonly ok: true; readonly accounts: ReadonlyArray<EmailAccountSummary> }
  | CommsLaneRefusal;

export type EmailThreadsOutcome =
  | {
      readonly ok: true;
      readonly threads: ReadonlyArray<Omit<EmailThreadSummary, "accountAddress" | "accountId">>;
    }
  | CommsLaneRefusal;

/** The Worker's SafeSender, carried intact (CM22): a display name when the
 * sender offered one, and always the address. */
export interface AliasSender {
  readonly name: string | null;
  readonly address: string;
  /** W-e84c3da6: true when the Worker proved the From: address by a DKIM
   * signature from its own domain; false when it could not, and `address` is
   * then the envelope (bounce) sender. Absent when the Worker did not say. */
  readonly verified?: boolean;
}

export interface AliasMessageSummary {
  readonly messageId: string;
  readonly from: AliasSender;
  readonly subject: string;
  /** Unix epoch ms from the Worker; null only if a row arrives without it. */
  readonly receivedAt: number | null;
  /** The mailbox TTL boundary, epoch ms; null only if absent. */
  readonly expiresAt: number | null;
}

/** CM22: the live Worker sends `from` as {name, address}; a bare string is
 * tolerated as an address-only sender, never rendered as an empty one. */
export function parseAliasSender(value: unknown): AliasSender {
  if (typeof value === "string" && value.trim().length > 0) {
    return { name: null, address: value.trim() };
  }
  if (typeof value === "object" && value !== null && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    if (typeof record["address"] === "string" && record["address"].trim().length > 0) {
      const name =
        typeof record["name"] === "string" && record["name"].trim().length > 0
          ? record["name"]
          : null;
      const verified = record["verified"];
      return typeof verified === "boolean"
        ? { name, address: record["address"], verified }
        : { name, address: record["address"] };
    }
  }
  return { name: null, address: "" };
}

export type AliasMessagesOutcome =
  | {
      readonly ok: true;
      readonly messages: ReadonlyArray<AliasMessageSummary>;
      /** W-1dcdcf29: the World's one plain sentence when the person's Inbox
       * cannot be shown yet (and why); null when it can. Not a refusal. */
      readonly unavailable: string | null;
    }
  | CommsLaneRefusal;

/** CM27 — one held email message at the authority's stored fidelity:
 * a snippet, never a claimed full body. */
export interface EmailThreadMessage {
  readonly providerMessageId: string;
  readonly direction: string;
  readonly from: string;
  readonly to: string;
  readonly snippet: string;
  readonly receivedAt: number | null;
}

export interface EmailThreadRead {
  readonly threadId: string;
  /** CM28: Gmail's own thread id — the open-in-Gmail door's coordinate. */
  readonly providerThreadId: string;
  readonly subject: string;
  readonly messages: ReadonlyArray<EmailThreadMessage>;
}

/** CM28 — one message's full text, fetched live and never stored. */
export type EmailBodyOutcome =
  | { readonly ok: true; readonly providerMessageId: string; readonly text: string }
  | CommsLaneRefusal;

export type EmailThreadReadOutcome =
  | { readonly ok: true; readonly thread: EmailThreadRead }
  | CommsLaneRefusal;

/** CM25 — one full alias message: the summary plus its stored text. */
export interface AliasMessageRecord extends AliasMessageSummary {
  readonly text: string;
}

export type AliasMessageReadOutcome =
  | { readonly ok: true; readonly message: AliasMessageRecord }
  | CommsLaneRefusal;

export type AliasMessageDeleteOutcome =
  | { readonly ok: true; readonly deleted: boolean; readonly messageId: string }
  | CommsLaneRefusal;

export type MarkReadOutcome =
  | { readonly ok: true; readonly lastReadSeq: number }
  | CommsLaneRefusal;

export async function markConversationRead(
  worldKey: string,
  conversationId: string,
  upToSeq: number,
  fetchImpl: typeof fetch = fetch,
): Promise<MarkReadOutcome> {
  const outcome = await call(
    "/api/neucleos/comms/mark-read",
    { worldKey, conversationId, upToSeq },
    fetchImpl,
  );
  if (isRefusal(outcome)) return outcome;
  const lastReadSeq = outcome["lastReadSeq"];
  if (typeof lastReadSeq !== "number") {
    return { ok: false, kind: "transport", reason: "The lane returned an invalid read receipt." };
  }
  return { ok: true, lastReadSeq };
}

export type ConversationReceiptVisibilityOutcome =
  | { readonly ok: true; readonly receiptVisibility: ReceiptVisibility }
  | CommsLaneRefusal;

/** CM31 — flip the caller's receipt sharing for one conversation over the
 * CM16 route; the control lands on the lane's own answer, never assumed. */
export async function setConversationReceiptVisibility(
  worldKey: string,
  conversationId: string,
  receiptVisibility: ReceiptVisibility,
  fetchImpl: typeof fetch = fetch,
): Promise<ConversationReceiptVisibilityOutcome> {
  const outcome = await call(
    "/api/neucleos/comms/receipt-visibility",
    { worldKey, conversationId, receiptVisibility },
    fetchImpl,
  );
  if (isRefusal(outcome)) return outcome;
  const answered = outcome["receiptVisibility"];
  if (answered !== "shared" && answered !== "private") {
    return {
      ok: false,
      kind: "transport",
      reason: "The lane returned an invalid receipt visibility.",
    };
  }
  return { ok: true, receiptVisibility: answered };
}

export type ReceiptsDefaultOutcome =
  | { readonly ok: true; readonly receiptsDefault: ReceiptVisibility }
  | CommsLaneRefusal;

function parsedReceiptsDefault(
  outcome: Record<string, unknown> | CommsLaneRefusal,
): ReceiptsDefaultOutcome {
  if (isRefusal(outcome)) return outcome;
  const answered = outcome["receiptsDefault"];
  if (answered !== "shared" && answered !== "private") {
    return {
      ok: false,
      kind: "transport",
      reason: "The lane returned an invalid receipts default.",
    };
  }
  return { ok: true, receiptsDefault: answered };
}

/** CM33 — the caller's World-wide receipts default over the lane. */
export async function readReceiptsDefault(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ReceiptsDefaultOutcome> {
  return parsedReceiptsDefault(
    await call("/api/neucleos/comms/receipts-default", { worldKey }, fetchImpl),
  );
}

/** CM33 — set the caller's World-wide receipts default; the control lands
 * on the lane's own answer, never assumed. */
export async function setReceiptsDefault(
  worldKey: string,
  receiptsDefault: ReceiptVisibility,
  fetchImpl: typeof fetch = fetch,
): Promise<ReceiptsDefaultOutcome> {
  return parsedReceiptsDefault(
    await call(
      "/api/neucleos/comms/set-receipts-default",
      { worldKey, receiptsDefault },
      fetchImpl,
    ),
  );
}

export async function listPresence(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<PresenceListOutcome> {
  const outcome = await call("/api/neucleos/presence/list", { worldKey }, fetchImpl);
  if (isRefusal(outcome)) return outcome;
  const rows = outcome["participants"];
  if (!Array.isArray(rows)) {
    return { ok: false, kind: "transport", reason: "The lane returned an invalid presence list." };
  }
  const participants: PresenceParticipant[] = [];
  for (const row of rows) {
    if (typeof row !== "object" || row === null) continue;
    const record = row as Record<string, unknown>;
    if (typeof record["actorId"] !== "string" || typeof record["online"] !== "boolean") continue;
    participants.push({ actorId: record["actorId"], online: record["online"] });
  }
  return { ok: true, participants };
}

// ————— CM30 additions: the member's own presence — the heartbeat that makes
// an open surface present, and the visibility flip behind the CM6 gates.

export type PresenceHeartbeatOutcome =
  | { readonly ok: true; readonly hidden: boolean }
  | CommsLaneRefusal;

export async function presenceHeartbeat(
  worldKey: string,
  sessionId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<PresenceHeartbeatOutcome> {
  const outcome = await call(
    "/api/neucleos/presence/heartbeat",
    { worldKey, sessionId },
    fetchImpl,
  );
  if (isRefusal(outcome)) return outcome;
  const hidden = outcome["hidden"];
  if (typeof hidden !== "boolean") {
    return { ok: false, kind: "transport", reason: "The lane returned an invalid heartbeat." };
  }
  return { ok: true, hidden };
}

export type PresenceVisibility = "visible" | "hidden";

export type PresenceVisibilityOutcome =
  | { readonly ok: true; readonly visibility: PresenceVisibility }
  | CommsLaneRefusal;

export async function setPresenceVisibility(
  worldKey: string,
  visibility: PresenceVisibility,
  fetchImpl: typeof fetch = fetch,
): Promise<PresenceVisibilityOutcome> {
  const outcome = await call(
    "/api/neucleos/presence/visibility",
    { worldKey, visibility },
    fetchImpl,
  );
  if (isRefusal(outcome)) return outcome;
  const answered = outcome["visibility"];
  if (answered !== "visible" && answered !== "hidden") {
    return { ok: false, kind: "transport", reason: "The lane returned an invalid visibility." };
  }
  return { ok: true, visibility: answered };
}

export async function listEmailAccounts(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<EmailAccountsOutcome> {
  const outcome = await call("/api/neucleos/email/accounts", { worldKey }, fetchImpl);
  if (isRefusal(outcome)) return outcome;
  const rows = outcome["accounts"];
  if (!Array.isArray(rows)) {
    return { ok: false, kind: "transport", reason: "The lane returned an invalid account list." };
  }
  const accounts: EmailAccountSummary[] = [];
  for (const row of rows) {
    if (typeof row !== "object" || row === null) continue;
    const record = row as Record<string, unknown>;
    if (typeof record["accountId"] !== "string" || typeof record["address"] !== "string") continue;
    accounts.push({
      accountId: record["accountId"],
      address: record["address"],
      status: typeof record["status"] === "string" ? record["status"] : "active",
    });
  }
  return { ok: true, accounts };
}

export async function listEmailThreads(
  worldKey: string,
  accountId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<EmailThreadsOutcome> {
  const outcome = await call("/api/neucleos/email/threads", { worldKey, accountId }, fetchImpl);
  if (isRefusal(outcome)) return outcome;
  const rows = outcome["threads"];
  if (!Array.isArray(rows)) {
    return { ok: false, kind: "transport", reason: "The lane returned an invalid thread list." };
  }
  const threads: Array<Omit<EmailThreadSummary, "accountAddress" | "accountId">> = [];
  for (const row of rows) {
    if (typeof row !== "object" || row === null) continue;
    const record = row as Record<string, unknown>;
    if (typeof record["threadId"] !== "string" || typeof record["subject"] !== "string") continue;
    threads.push({
      threadId: record["threadId"],
      subject: record["subject"],
      lastMessageAt: typeof record["lastMessageAt"] === "number" ? record["lastMessageAt"] : 0,
    });
  }
  return { ok: true, threads };
}

export async function listAliasMessages(
  worldKey: string,
  fetchImpl: typeof fetch = fetch,
): Promise<AliasMessagesOutcome> {
  const outcome = await call("/api/neucleos/inbox/alias-messages", { worldKey }, fetchImpl);
  if (isRefusal(outcome)) return outcome;
  const rows = outcome["messages"];
  if (!Array.isArray(rows)) {
    return { ok: false, kind: "transport", reason: "The lane returned an invalid alias list." };
  }
  const messages: AliasMessageSummary[] = [];
  for (const row of rows) {
    if (typeof row !== "object" || row === null) continue;
    const record = row as Record<string, unknown>;
    if (typeof record["messageId"] !== "string") continue;
    messages.push({
      messageId: record["messageId"],
      from: parseAliasSender(record["from"]),
      subject: typeof record["subject"] === "string" ? record["subject"] : "",
      receivedAt: typeof record["receivedAt"] === "number" ? record["receivedAt"] : null,
      expiresAt: typeof record["expiresAt"] === "number" ? record["expiresAt"] : null,
    });
  }
  const unavailable = outcome["unavailable"];
  return {
    ok: true,
    messages,
    unavailable:
      typeof unavailable === "string" && unavailable.trim().length > 0 ? unavailable : null,
  };
}

/** CM27 — read one email thread's held messages through the lane route. */
export async function readEmailThread(
  worldKey: string,
  threadId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<EmailThreadReadOutcome> {
  const outcome = await call("/api/neucleos/email/read", { worldKey, threadId }, fetchImpl);
  if (isRefusal(outcome)) return outcome;
  const rows = outcome["messages"];
  if (typeof outcome["threadId"] !== "string" || !Array.isArray(rows)) {
    return { ok: false, kind: "transport", reason: "The lane returned an invalid email thread." };
  }
  const messages: EmailThreadMessage[] = [];
  for (const row of rows) {
    if (typeof row !== "object" || row === null) continue;
    const record = row as Record<string, unknown>;
    if (typeof record["providerMessageId"] !== "string") continue;
    const to = record["to"];
    messages.push({
      providerMessageId: record["providerMessageId"],
      direction: typeof record["direction"] === "string" ? record["direction"] : "inbound",
      from: typeof record["from"] === "string" ? record["from"] : "",
      to:
        typeof to === "string"
          ? to
          : Array.isArray(to)
            ? to.filter((item): item is string => typeof item === "string").join(", ")
            : "",
      snippet: typeof record["snippet"] === "string" ? record["snippet"] : "",
      receivedAt: typeof record["receivedAt"] === "number" ? record["receivedAt"] : null,
    });
  }
  return {
    ok: true,
    thread: {
      threadId: outcome["threadId"],
      providerThreadId:
        typeof outcome["providerThreadId"] === "string" ? outcome["providerThreadId"] : "",
      subject: typeof outcome["subject"] === "string" ? outcome["subject"] : "",
      messages,
    },
  };
}

/** CM28 — read one message's full text through the lane route. */
export async function readEmailMessageBody(
  worldKey: string,
  threadId: string,
  providerMessageId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<EmailBodyOutcome> {
  const outcome = await call(
    "/api/neucleos/email/body",
    { worldKey, threadId, providerMessageId },
    fetchImpl,
  );
  if (isRefusal(outcome)) return outcome;
  if (typeof outcome["providerMessageId"] !== "string" || typeof outcome["text"] !== "string") {
    return { ok: false, kind: "transport", reason: "The lane returned an invalid message body." };
  }
  return { ok: true, providerMessageId: outcome["providerMessageId"], text: outcome["text"] };
}

/** CM25 — read one alias message in full through the existing lane route. */
export async function readAliasMessage(
  worldKey: string,
  messageId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<AliasMessageReadOutcome> {
  const outcome = await call("/api/neucleos/inbox/alias-read", { worldKey, messageId }, fetchImpl);
  if (isRefusal(outcome)) return outcome;
  if (typeof outcome["messageId"] !== "string" || typeof outcome["text"] !== "string") {
    return {
      ok: false,
      kind: "transport",
      reason: "The lane returned an invalid alias message.",
    };
  }
  return {
    ok: true,
    message: {
      messageId: outcome["messageId"],
      from: parseAliasSender(outcome["from"]),
      subject: typeof outcome["subject"] === "string" ? outcome["subject"] : "",
      receivedAt: typeof outcome["receivedAt"] === "number" ? outcome["receivedAt"] : null,
      expiresAt: typeof outcome["expiresAt"] === "number" ? outcome["expiresAt"] : null,
      text: outcome["text"],
    },
  };
}

/** CM25 — delete one alias message through the existing lane route. */
export async function deleteAliasMessage(
  worldKey: string,
  messageId: string,
  fetchImpl: typeof fetch = fetch,
): Promise<AliasMessageDeleteOutcome> {
  const outcome = await call(
    "/api/neucleos/inbox/alias-delete",
    { worldKey, messageId },
    fetchImpl,
  );
  if (isRefusal(outcome)) return outcome;
  const deleted = outcome["deleted"];
  const messageIdBack = outcome["messageId"];
  if (typeof deleted !== "boolean" || typeof messageIdBack !== "string") {
    return {
      ok: false,
      kind: "transport",
      reason: "The lane returned an invalid alias delete receipt.",
    };
  }
  return { ok: true, deleted, messageId: messageIdBack };
}

export async function sendConversationMessage(
  worldKey: string,
  conversationId: string,
  body: string,
  fetchImpl: typeof fetch = fetch,
  clientMessageId?: string,
): Promise<ConversationSendOutcome> {
  const outcome = await call(
    "/api/neucleos/comms/send",
    {
      worldKey,
      conversationId,
      body,
      ...(clientMessageId === undefined ? {} : { clientMessageId }),
    },
    fetchImpl,
  );
  if (isRefusal(outcome)) return outcome;
  const seq = outcome["seq"];
  if (typeof seq !== "number") {
    return {
      ok: false,
      kind: "transport",
      reason: "The conversations lane returned an invalid send receipt.",
    };
  }
  return { ok: true, seq };
}
